import React, { useContext, useEffect, useState } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import { MapContext } from '../MapContext';
import { LayerContext } from '../LayerContext';
import LayerAccordion from './LayerAccordion';
import fetchBookmark from '../NavBar/FetchBookmark';
import { loadLayerAndDispatch, PREFIX_TO_ASSET_TYPE } from '../SideBar/AddAssetButton';
import { useUser } from '../UserContext';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import './LayerList.css';

const LayerList = ({ layerProps, setLayerProps }) => {
  const { map } = useContext(MapContext);
  const { user } = useUser();
  const { layers, dispatch } = useContext(LayerContext);
  const [bookmarkLoaded, setBookmarkLoaded] = useState(false);
  const [bookmarkStyles, setBookmarkStyles] = useState([]);

  const fetchBookmarkAndDispatch = async () => {
    try {
      // fetch layer information from the bookmark
      const authMode = user ? 'userPool' : 'iam';
      const bookmark = await fetchBookmark(authMode);
      bookmark && console.log("bookmark:", bookmark)
      if (bookmark && !bookmarkLoaded) {
        // set view state
        map.current.flyTo(
          { ...bookmark?.viewState, speed: 5 });
        // rendering background layers
        bookmark?.layers && (async () => {
          for (const layer of bookmark?.layers) {
            // Await the execution of loadLayerAndDispatch for each layer
            await loadLayerAndDispatch(
              map.current,
              layer.assetType === "gene_group" ? layer.subLayers.map(l => l.assetUrl) : layer.assetUrl,
              PREFIX_TO_ASSET_TYPE[layer.assetType],
              dispatch,
              false,
              '',
              layer.layerId
            );
          }
          setBookmarkLoaded(true);
          setBookmarkStyles(bookmark.layers);
        }
        )(); // Immediately invoke the async function
      }
    } catch (err) {
      console.error('Error dispatch layers in the bookmark', err);
    }
  };

  useEffect(() => {
    // fetch bookmark if user signed in
    if (map && map.current) {
      fetchBookmarkAndDispatch();
    }
  }, [map, user]);

  useEffect(() => {
    // update props when layers updated
    const updatedProps = layers.map(layer => {
      // get bookmark props if exists
      const bookmarkProps = bookmarkStyles.find(l => l.layerId === layer.id);
      // Check the existing states for layers
      const existingLayerProps = layerProps.find(lv => lv.layerId === layer.id);
      // title
      const layerTitle = bookmarkProps ? bookmarkProps.layerTitle : (existingLayerProps ? existingLayerProps.layerTitle : (layer?.assetType === "gene_group" ? (layer?.title?.slice(0, 3)?.join('/') + (layer?.title?.length > 3 ? '...' : "")) : layer?.title));
      // layer visibility from bookmark
      const visibility = bookmarkProps ? bookmarkProps.visibility : (existingLayerProps ? existingLayerProps.visibility : true);
      const expanded = existingLayerProps ? existingLayerProps.expanded : false;
      // circle color for gene or gene group
      const circleColor = bookmarkProps ? bookmarkProps.circleColor : null;
      return {
        layerId: layer.id,
        layerTitle: layerTitle,
        visibility: visibility,
        expanded: expanded,
        color: circleColor,
      };
    });
    // Update the layerProps state
    setLayerProps(updatedProps);
  }, [layers, bookmarkStyles]);

  const moveLayer = (id, targetLayer) => {
    if (targetLayer.assetType === "gene_group") {
      map.current.moveLayer(id, targetLayer.subLayerIds[targetLayer.subLayerIds.length - 1]);
    } else {
      map.current.moveLayer(id, targetLayer.id);
    }
  }

  const moveLayerUp = (event, currentLayer) => {
    event.stopPropagation();
    dispatch({ type: 'MOVE_LAYER_UP', payload: currentLayer.id });
    const targetLayer = layers[layers.findIndex(layer => layer.id === currentLayer.id) + 2] || "gl-draw-line.cold";
    if (targetLayer && map && map.current && map.current.moveLayer) {
      if (currentLayer.assetType === "gene_group") {
        currentLayer?.subLayerIds?.map(id => moveLayer(id, targetLayer));
      } else {
        moveLayer(currentLayer.id, targetLayer);
      }
    }
  };

  const moveLayerDown = (event, currentLayer) => {
    event.stopPropagation();
    dispatch({ type: 'MOVE_LAYER_DOWN', payload: currentLayer.id });
    const targetLayer = layers[layers.findIndex(layer => layer.id === currentLayer.id) - 1];
    if (targetLayer && map && map.current && map.current.moveLayer) {
      if (currentLayer.assetType === "gene_group") {
        currentLayer?.subLayerIds?.map(id => moveLayer(id, targetLayer));
      } else {
        moveLayer(currentLayer.id, targetLayer);
      }
    }
  };

  const handleClickAllExpanded = () => {
    const updatedLayerProps = layerProps.map(layer => ({
      ...layer,
      expanded: true
    }));
    setLayerProps(updatedLayerProps);
  }

  const handleClickAllClosed = () => {
    const updatedLayerProps = layerProps.map(layer => ({
      ...layer,
      expanded: false
    }));
    setLayerProps(updatedLayerProps);
  }

  return (
    <div className="layerList-container">
      <List className="layerList-list">
        <ListItem className="layerList-listItem">
          <ListItemIcon className="layerList-header">
            <MapIcon className="layerList-icon" />
          </ListItemIcon>
          <ListItemText primary="Current Layers" />
          <KeyboardArrowUpIcon
            className='layerList-collase-all'
            onClick={handleClickAllClosed}
          />
          <KeyboardArrowDownIcon
            className='layerList-collase-all'
            onClick={handleClickAllExpanded}
          />
        </ListItem>
        {layers.slice().reverse().map((layer, index, arr) => (
          <LayerAccordion
            key={`accordion-${layer.id}`}
            layer={layer}
            layerProps={layerProps}
            index={index}
            layerCount={arr.length}
            moveLayerUp={moveLayerUp}
            moveLayerDown={moveLayerDown}
            setLayerProps={setLayerProps}
            map={map}
            styles={bookmarkStyles.find(l => l.layerId === layer.id)}
          />
        ))}
      </List>
    </div>
  );
}

export default LayerList;

